/**
 * FirstSessionGuide.js — full-screen modal that walks a brand-new teen
 * through HiBFF on their first dashboard load. Renders the bubbles
 * returned by GET /api/onboarding/first-session sequentially, AvA-style.
 *
 * Lifecycle:
 *  • Polls the guide endpoint when Dashboard.js mounts.
 *  • If `completed_before === true` the modal stays hidden.
 *  • Otherwise it appears once, the teen taps through the bubbles, and
 *    on the final bubble taps "Start practising" → POST /complete.
 *  • A small "Skip" link in the corner lets them dismiss without missing
 *    the welcome — completion is still recorded (skipped=true) so the
 *    modal doesn't re-appear.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Heart, Trophy, Gem, Shield, BookOpen, Volume2, VolumeX } from 'lucide-react';
import useAdultMode from '../../hooks/useAdultMode';
import { TEEN_VOICES } from '../../pages/avaLibrary/data';

const ADULT_BUBBLES = [
  { id: 'welcome',  title: 'Welcome — this is your private practice studio',
    body: "You're here to rehearse the conversations that move careers and relationships. Every session is private, judgement-free, and tuned for adults." },
  { id: 'practice', title: 'Rehearse high-stakes moments',
    body: "Salary reviews, investor pitches, difficult 1:1s, boundary talks — pick a scenario, the AvA plays the other side, and you practise until you're calm and sharp." },
  { id: 'tone',     title: 'See your tone, pace and clarity',
    body: 'After each rep you get a breakdown of tone, hesitation and persuasive markers — coach yourself between sessions.' },
  { id: 'mastery',  title: 'Earn Mastery Tokens',
    body: 'Complete scenario tracks to mint Verifiable Skills Badges. Add them straight to your LinkedIn Certifications when the 6-month vault unlocks.' },
  { id: 'credits',  title: 'Pro Credits, not Hearts',
    body: 'Your in-app currency for premium scenario packs and extra voice minutes. No teen language, no gamified clutter.' },
];
import { apiClient } from '../../api/client';

const ICONS = {
  welcome: Sparkles,
  how_we_practise: Sparkles,
  challenges: Trophy,
  hearts: Heart,
  badges: Trophy,
  nft_rewards: Gem,
  nft_rewards_adult: Gem,
  nft_independence: Shield,
  charter_active: Shield,
  charter_pending_parent: Shield,
  charter_pending_teen: Shield,
  charter_will_arrive: Shield,
  first_action: ArrowRight,
};

const BASE_OPENAI_VOICES = { nova: 1, shimmer: 1, coral: 1, alloy: 1, echo: 1, ash: 1, fable: 1, onyx: 1, sage: 1 };
const stripEmoji = (t) => (t || '').replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '').trim();

const FirstSessionGuide = () => {
  const navigate = useNavigate();
  const isAdultMode = useAdultMode();
  const [guide, setGuide] = useState(null);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [greeter, setGreeter] = useState({ name: 'Maya', avatar_url: null, voice: 'nova' });
  const [muted, setMuted] = useState(() => localStorage.getItem('hibff_guide_muted') === '1');
  const audioRef = useRef(null);
  const audioCacheRef = useRef(new Map());

  // Maya's photo + voice — same public greeter config as the 22-question
  // registration wizard, so the guide feels like Maya never left.
  useEffect(() => {
    apiClient.request('/api/ava-video/registration-greeter')
      .then(d => { if (d && d.name) setGreeter(g => ({ ...g, ...d })); })
      .catch(() => {});
  }, []);

  const stopSpeech = useCallback(() => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } audioRef.current = null; }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(m => {
      const next = !m;
      localStorage.setItem('hibff_guide_muted', next ? '1' : '0');
      if (next) stopSpeech();
      return next;
    });
  }, [stopSpeech]);

  // Speak the current bubble in Maya's real voice (OpenAI TTS), just like
  // during registration. Cached per line; silent failures fall back to text.
  useEffect(() => {
    if (!open || muted || !guide) return undefined;
    const b = guide.bubbles[index];
    const spoken = stripEmoji(b?.text);
    if (!spoken) return undefined;
    const slot = TEEN_VOICES.find(v => v.id === greeter.voice);
    const ttsVoice = slot?.baseVoice || (BASE_OPENAI_VOICES[greeter.voice] ? greeter.voice : 'nova');
    const pitch = slot?.pitch || 1.0;
    let cancelled = false;
    const key = `${ttsVoice}|${spoken}`;
    if (!audioCacheRef.current.has(key)) {
      audioCacheRef.current.set(
        key,
        apiClient.request('/api/tts/generate', {
          method: 'POST', body: JSON.stringify({ text: spoken, voice: ttsVoice }),
        }).then(d => (d && d.success && d.audio) ? d.audio : null)
          .catch(() => { audioCacheRef.current.delete(key); return null; })
      );
    }
    audioCacheRef.current.get(key).then(b64 => {
      if (cancelled || !b64) return;
      stopSpeech();
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      if (pitch !== 1.0) { audio.preservesPitch = false; audio.playbackRate = pitch; }
      audioRef.current = audio;
      audio.play().catch(() => { /* autoplay blocked — text still shows */ });
    });
    return () => { cancelled = true; stopSpeech(); };
  }, [open, muted, index, guide, greeter.voice, stopSpeech]);

  useEffect(() => {
    let cancelled = false;
    apiClient.request('/api/onboarding/first-session')
      .then(data => {
        if (cancelled || !data) return;
        // Phase D — adult-mode replaces the teen bubble sequence entirely.
        const finalGuide = isAdultMode
          ? { ...data, bubbles: ADULT_BUBBLES, persona: 'adult' }
          : data;
        setGuide(finalGuide);
        if (!data.completed_before) setOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // Safe Addition: `isAdultMode` is stable per session (a parent-derived
    // boolean). Listing it means: if the parent ever hot-swaps the mode
    // mid-mount, we correctly refetch the guide with the right persona.
    // In practice this rarely fires because the mode is fixed at login.
  }, [isAdultMode]);

  const markComplete = useCallback(async (skipped = false) => {
    stopSpeech();
    setFinishing(true);
    try {
      await apiClient.request('/api/onboarding/first-session/complete', {
        method: 'POST', body: JSON.stringify({ skipped }),
      });
    } catch { /* still close the modal */ }
    setOpen(false);
    setFinishing(false);
  }, [stopSpeech]);

  if (!guide || !open) return null;
  const bubble = guide.bubbles[index];
  const isLast = index === guide.bubbles.length - 1;
  const Icon = ICONS[bubble.id] || Sparkles;
  const progress = ((index + 1) / guide.bubbles.length) * 100;

  return (
    <div
      data-testid="first-session-guide"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
    >
      <div
        className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh' }}
      >
        {/* Progress bar + skip */}
        <div className="px-5 pt-4 pb-2 flex items-center justify-between">
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3E8DC' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #F5831F, #FFB073)' }}
            />
          </div>
          <span
            className="ml-3 text-[11px] font-semibold whitespace-nowrap"
            style={{ color: '#8A7A63' }}
            data-testid="first-session-step-count"
          >
            Step {index + 1} of {guide.bubbles.length}
          </span>
          <button
            onClick={() => markComplete(true)}
            disabled={finishing}
            data-testid="first-session-skip"
            className="ml-4 text-xs font-medium px-2 py-1 rounded hover:bg-gray-100"
            style={{ color: '#666666' }}
          >
            Skip
          </button>
        </div>

        {/* Maya avatar header — the same Practice Manager from registration */}
        <div className="px-6 pt-3 pb-2 flex items-center gap-3">
          {greeter.avatar_url ? (
            <img
              src={greeter.avatar_url}
              alt="Maya"
              data-testid="guide-maya-avatar"
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 shadow-md border-2"
              style={{ borderColor: '#F5831F' }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-md"
              style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
            >
              <span className="text-2xl">✨</span>
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>{guide.ava_name}</p>
            <p className="text-[11px]" style={{ color: '#999' }}>{guide.ava_role || 'Your Practice Manager'}</p>
          </div>
          <button
            onClick={toggleMute}
            data-testid="guide-mute-toggle"
            aria-label={muted ? 'Unmute Maya' : 'Mute Maya'}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            style={{ color: muted ? '#999' : '#F5831F' }}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>

        {index === 0 && (
          <div
            className="mx-6 mt-1 px-3 py-2 rounded-xl flex items-start gap-2"
            style={{ backgroundColor: '#FFF5EA' }}
            data-testid="first-session-read-tip"
          >
            <BookOpen size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#F5831F' }} />
            <p className="text-xs leading-snug" style={{ color: '#7A6B55' }}>
              Worth a minute: there are {guide.bubbles.length} short steps — reading each one will make your whole dashboard make sense.
            </p>
          </div>
        )}

        {/* Bubble content */}
        <div
          className="px-6 py-5 flex-1 overflow-y-auto"
          data-testid={`bubble-${bubble.id}`}
        >
          {bubble.heading && (
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#FFF5EA' }}
              >
                <Icon size={14} style={{ color: '#F5831F' }} />
              </div>
              <h3 className="text-base font-bold" style={{ color: '#1A1209' }}>{bubble.heading}</h3>
            </div>
          )}
          <p
            className="text-[15px] leading-relaxed"
            style={{ color: '#1A1209' }}
          >
            {bubble.text}
          </p>
          {bubble.cta && (
            <button
              onClick={() => { markComplete(false); navigate(bubble.cta.route); }}
              data-testid={`bubble-cta-${bubble.id}`}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors"
              style={{ borderColor: '#F5831F', color: '#F5831F' }}
            >
              {bubble.cta.label} <ArrowRight size={14} />
            </button>
          )}
        </div>

        {/* Pagination dots + Next button */}
        <div className="px-6 py-4 flex items-center justify-between border-t" style={{ borderColor: '#F3E8DC' }}>
          <div className="flex items-center gap-1.5">
            {guide.bubbles.map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-colors"
                style={{ backgroundColor: i === index ? '#F5831F' : '#E5DDD0' }}
              />
            ))}
          </div>
          {isLast ? (
            <button
              onClick={() => markComplete(false)}
              disabled={finishing}
              data-testid="first-session-start"
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white flex items-center gap-2 transition-colors"
              style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
            >
              Start practising <Sparkles size={14} />
            </button>
          ) : (
            <button
              onClick={() => setIndex(i => Math.min(guide.bubbles.length - 1, i + 1))}
              data-testid="first-session-next"
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white flex items-center gap-2 transition-colors"
              style={{ backgroundColor: '#1A1209' }}
            >
              Next <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirstSessionGuide;
